import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getRoofs, 
  getWalls, 
  getCanopies, 
  getAccessories, 
  updateAccessoryPrice, 
  updateBasePrice, 
  updateWallPrice,
  getBasePrices,
  getWallPrices,
  getUsers,
  updateUserRole,
  deleteUser,
  getCompanyProfile,
  updateCompanyProfile,
  inviteUser,
  uploadDealerLogo 
} from '../services/apiService';
import { logout } from '../services/authService';
import { jwtDecode } from 'jwt-decode';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, TextField, Avatar, Chip, 
  IconButton, Menu, MenuItem, Tabs, Tab, List, ListItem, 
  ListItemIcon, ListItemText, ListItemButton, Divider, Grid, 
  Alert, Snackbar, CircularProgress, Dialog, DialogActions, 
  DialogContent, DialogContentText, DialogTitle, ToggleButton, 
  ToggleButtonGroup, InputAdornment
} from '@mui/material';

import {
  People, Inventory, Business, MoreVert, Add, CloudUpload, 
  AttachMoney, Logout, Language, Phone, LocationOn, Receipt
} from '@mui/icons-material';
import ChangePasswordForm from './ChangePasswordForm';
import { t } from '../i18n/useTranslation';

const productNameTranslations = {
  // Canopy types
  'wall mounted canopy':        { nl: 'Wandgemonteerde Overkapping', en: 'Wall Mounted Canopy' },
  'wall_mounted':               { nl: 'Wandgemonteerde Overkapping', en: 'Wall Mounted Canopy' },
  'free standing canopy':       { nl: 'Vrijstaande Overkapping', en: 'Free Standing Canopy' },
  'free_standing':              { nl: 'Vrijstaande Overkapping', en: 'Free Standing Canopy' },

  // Roof types
  'sloped glass roof':          { nl: 'Schuin Glazen Dak', en: 'Sloped Glass Roof' },
  'flat glass roof':            { nl: 'Plat Glazen Dak', en: 'Flat Glass Roof' },
  'poly':                       { nl: 'Polycarbonaat', en: 'Polycarbonate' },
  'louvred':                    { nl: 'Lamellendak', en: 'Louvred' },
  'retracting':                 { nl: 'Uitschuifbaar Dak', en: 'Retracting' },

  // Walls
  'sliding_glass_door':         { nl: 'Schuifglasdeur', en: 'Sliding Glass Door' },
  'glass_walls':                { nl: 'Glazen Wanden', en: 'Glass Walls' },
  'aluminium_walls':            { nl: 'Aluminium Wanden', en: 'Aluminium Walls' },
  'fixed_wall_louvres':         { nl: 'Vaste Wandlamellen', en: 'Fixed Wall Louvres' },
  'opening_closing_wall_louvres': { nl: 'Kantelbare Lamellenwand', en: 'Tiltable Wall Louvres' },
  'wall_blinds_zip':            { nl: 'Wandzonwering Zip', en: 'Wall Blinds (Zip)' },

  // Accessories
  'led lighting':               { nl: 'LED Verlichting', en: 'LED Lighting' },
  'led_lighting':               { nl: 'LED Verlichting', en: 'LED Lighting' },
  'heating system':             { nl: 'Verwarmingssysteem', en: 'Heating System' },
  'heating_system':             { nl: 'Verwarmingssysteem', en: 'Heating System' },
  'installation':               { nl: 'Installatie', en: 'Installation' },
  'rain sensor':                { nl: 'Regensensor', en: 'Rain Sensor' },
  'rain_sensor':                { nl: 'Regensensor', en: 'Rain Sensor' },
};

const translateName = (name, lang = 'en') => {
  if (!name) return name;
  const parts = name.split(' + ');
  const translatedParts = parts.map(part => {
    const cleanKey = part.trim().toLowerCase();
    const entry = productNameTranslations[cleanKey];
    if (entry && entry[lang]) return entry[lang];
    // Fallback: remove underscores and capitalize
    return part.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  });
  return translatedParts.join(' + ');
};

const Settings = ({ lang = 'en' }) => {
  const navigate = useNavigate();
  
  const getUserRole = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      const roleClaimType = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
      const rawRole = decoded.role || decoded[roleClaimType] || localStorage.getItem('userRole');
      const userRoles = Array.isArray(rawRole) ? rawRole : (rawRole ? [rawRole] : []);
      return userRoles;
    } catch (e) {
      console.error('Token decode error:', e);
      return localStorage.getItem('userRole');
    }
  };

  const userRole = getUserRole();

  // Handle array of roles
  const isSuperAdmin = Array.isArray(userRole) 
    ? userRole.includes('SUPERADMIN') 
    : userRole === 'SUPERADMIN';

  const isAdmin = Array.isArray(userRole)
    ? (userRole.includes('ADMIN') || userRole.includes('SUPERADMIN'))
    : (userRole === 'ADMIN' || isSuperAdmin);

  const [activeModule, setActiveModule] = useState(isAdmin ? 'users' : 'security');
  
  const [isPriceEditorOpen, setIsPriceEditorOpen] = useState(false);
  const [productTab, setProductTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [productMenuAnchor, setProductMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false); 
  const [error, setError] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('front');
  
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({ name: '', email: '', password: '' });
  const [usersLoading, setUsersLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);

  const [walls, setWalls] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [bases, setBases] = useState([]);
  
  const [passwordValidation, setPasswordValidation] = useState({
    hasDigit: false, hasUpper: false, hasLower: false, minLength: false, isValid: false
  });

  const [companyProfile, setCompanyProfile] = useState({
    name: '', brandName: '', email: '', address: '', phone: '', website: '', vatNumber: '', currencySymbol: '£', logoUrl: null
  });

  // Fetch product data on mount
  useEffect(() => {
    const fetchProductData = async () => {
      if (!isAdmin) {
        setLoading(false);
        return;
      }

      const cacheKey = 'alfresko_settings_data';

      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          setAccessories(parsed.accessories || []);
          setBases(parsed.bases || []);
          setWalls(parsed.walls || []);
          setUsers(parsed.users || []);
          if (parsed.companyProfile) setCompanyProfile(parsed.companyProfile);
          
          setLoading(false);
          setUsersLoading(false);
        } else {
          setLoading(true);
          setUsersLoading(true);
        }
        
        setError('');
        const [roofsData, wallsData, wallPricesData, canopiesData, accessoriesData, basePricesData, usersData, companyData] = await Promise.all([
            getRoofs(), getWalls(), getWallPrices(), getCanopies(), getAccessories(), getBasePrices(), getUsers(), getCompanyProfile()
        ]);

        const accessoriesArray = Array.isArray(accessoriesData) 
          ? accessoriesData.map(a => ({
              id: a.id || a.Id,
              name: a.name || a.Name,
              alias: a.alias || a.Alias,
              isPerSqMeter: a.isPerSqMeter,
              isActive: a.isActive,
              price: a.price ?? 0 
            }))
          : [];

        const combinedWalls = (Array.isArray(wallsData) ? wallsData : []).map(wall => {
          const frontPrice = (Array.isArray(wallPricesData) ? wallPricesData : []).find(price => 
            price.wallId === wall.id && price.position === 'front'
          );
          const sidesPrice = (Array.isArray(wallPricesData) ? wallPricesData : []).find(price => 
            price.wallId === wall.id && price.position === 'sides'
          );
        
          return {
            ...wall,
            frontPrice: frontPrice ? frontPrice.price : 'N/A',
            frontPriceId: frontPrice ? frontPrice.id : null,
            sidesPrice: sidesPrice ? sidesPrice.price : 'N/A',
            sidesPriceId: sidesPrice ? sidesPrice.id : null
          };
        });

        const combinedBases = (Array.isArray(basePricesData) ? basePricesData : []).map(basePrice => {
            const canopy = (Array.isArray(canopiesData) ? canopiesData : []).find(c => c.id === basePrice.canopyId);
            const roof = (Array.isArray(roofsData) ? roofsData : []).find(r => r.id === basePrice.roofId);
            
            return {
              id: basePrice.id,
              canopyId: basePrice.canopyId,
              roofId: basePrice.roofId,
              name: `${canopy?.name || 'Unknown Canopy'} + ${roof?.name || 'Unknown Roof'}`,
              canopyName: canopy?.name || 'Unknown',
              roofName: roof?.name || 'Unknown',
              canopyAlias: canopy?.alias || 'N/A',
              roofAlias: roof?.alias || 'N/A',
              price: basePrice.totalPrice || basePrice.price || 0,
              priceId: basePrice.id 
            };
        });

        const usersArray = Array.isArray(usersData) ? usersData : [];
        
        let profileObj = companyProfile;
        if (companyData) {
            profileObj = {
                name: companyData.name || '',
                brandName: companyData.brandName || '',
                email: companyData.contactEmail || '',
                address: companyData.address || '',
                phone: companyData.phone || '',
                website: companyData.website || '',
                vatNumber: companyData.vatNumber || '',
                currencySymbol: companyData.currencySymbol || '£',
                logoUrl: companyData.logoUrl || null
            };
        }
        sessionStorage.setItem(cacheKey, JSON.stringify({
          accessories: accessoriesArray,
          bases: combinedBases,
          walls: combinedWalls,
          users: usersArray,
          companyProfile: profileObj
        }));
        setBases(combinedBases);
        setWalls(combinedWalls);
        setAccessories(accessoriesArray);
        setUsers(usersArray);
        setCompanyProfile(profileObj);
        setUsersLoading(false);

      } catch (err) {
        console.error('FAILED TO FETCH DATA:', err);
        if (!sessionStorage.getItem(cacheKey)) {
          setError(`Failed to load data: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const validatePassword = (password) => {
    const hasDigit = /\d/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const minLength = password.length >= 6;
    setPasswordValidation({ hasDigit, hasUpper, hasLower, minLength, isValid: hasDigit && hasUpper && hasLower && minLength });
  };

  const allNavigationItems = [
    { id: 'users', label: t('settings.nav.users'), icon: <People />, requiredRole: 'ADMIN' },
    { id: 'products', label: t('settings.nav.products'), icon: <Inventory />, requiredRole: 'ADMIN' },
    { id: 'profile', label: t('settings.nav.profile'), icon: <Business />, requiredRole: 'ADMIN' },
    { id: 'security', label: t('settings.nav.security'), icon: <Logout />, requiredRole: 'ALL' }
];

  const navigationItems = allNavigationItems.filter(item => {
      if (item.requiredRole === 'ALL') return true;
      if (item.requiredRole === 'ADMIN') return isAdmin;
      if (item.requiredRole === 'SUPERADMIN') return isSuperAdmin;
      return false;
  });

  const handleMenuClick = (event, user) => { setAnchorEl(event.currentTarget); setSelectedUser(user); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedUser(null); };

  const handleChangeRole = async (newRole) => {
    if (!selectedUser) return;
    const userId = selectedUser.id;
    
    try {
        const token = localStorage.getItem('authToken');
        const decodedToken = jwtDecode(token);
        if (userId === decodedToken.id && newRole !== 'ADMIN' && !isSuperAdmin) {
            alert("Warning: You are demoting yourself. You will be logged out.");
            await updateUserRole(userId, newRole);
            handleLogout(); 
            return; 
        }
    } catch(e) {}
    
    try {
      await updateUserRole(userId, newRole); 
      setUsers(currentUsers => currentUsers.map(user => user.id === userId ? { ...user, role: newRole } : user));
      setSnackbar({ open: true, message: `Role updated successfully`, severity: 'success' });
      sessionStorage.removeItem('alfresko_settings_data');
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update role.', severity: 'error' });
    } finally {
        handleRoleMenuClose();
    }
  };

  const handleRemoveUser = async () => {
    if (!selectedUser) return;
    if (typeof deleteUser !== 'function') {
        setSnackbar({ open: true, message: 'Feature not configured.', severity: 'warning' });
        handleMenuClose(); return;
    }
    
    if (!window.confirm(`Are you sure you want to remove ${selectedUser.name}?`)) { handleMenuClose(); return; }

    try {
        await deleteUser(selectedUser.id);
        setUsers(currentUsers => currentUsers.filter(user => user.id !== selectedUser.id));
        setSnackbar({ open: true, message: `User removed.`, severity: 'success' });
        sessionStorage.removeItem('alfresko_settings_data');
    } catch (error) {
        setSnackbar({ open: true, message: 'Failed to remove user.', severity: 'error' });
    } finally { handleMenuClose(); }
  };

  const handleRoleMenuOpen = (event) => setRoleMenuAnchorEl(event.currentTarget);
  const handleRoleMenuClose = () => { setRoleMenuAnchorEl(null); handleMenuClose(); };

  const handleProductMenuClick = (event, product, productType) => { setProductMenuAnchor(event.currentTarget); setSelectedProduct(product); setSelectedProductType(productType); };
  const handleProductMenuClose = () => setProductMenuAnchor(null);

  const handleUpdatePrice = () => {
    setSelectedPosition('front');
    let currentPrice = '';
    if (selectedProductType === 'wall') {
        currentPrice = selectedProduct.frontPrice !== 'N/A' ? selectedProduct.frontPrice : '';
    } else {
        currentPrice = (selectedProduct.price !== undefined && selectedProduct.price !== null) ? selectedProduct.price.toString() : '';
    }
    setNewPrice(currentPrice);
    setIsPriceEditorOpen(true);
    setProductMenuAnchor(null);
  };

  const handleClosePriceEditor = () => { setIsPriceEditorOpen(false); setSelectedProduct(null); };
  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleProfileChange = (field, value) => setCompanyProfile(prev => ({ ...prev, [field]: value }));

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const payload = {
          brandName: companyProfile.brandName,
          address: companyProfile.address,
          phone: companyProfile.phone,
          website: companyProfile.website,
          vatNumber: companyProfile.vatNumber,
          currencySymbol: companyProfile.currencySymbol
      };
      await updateCompanyProfile(null, payload);
      setSnackbar({ open: true, message: 'Profile saved!', severity: 'success' });
      sessionStorage.removeItem('alfresko_settings_data');
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save.', severity: 'error' });
    } finally { setLoading(false); }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setSnackbar({ open: true, message: 'Image files only.', severity: 'error' }); return; }

    try {
        setLoading(true);
        const result = await uploadDealerLogo(file);
        setCompanyProfile(prev => ({ ...prev, logoUrl: result.logoUrl }));
        setSnackbar({ open: true, message: 'Logo uploaded!', severity: 'success' });
    } catch (error) {
        setSnackbar({ open: true, message: 'Upload failed.', severity: 'error' });
    } finally { setLoading(false); }
  };

  const handleOpenInviteModal = () => { setInviteFormData({ name: '', email: '', password: '' }); setIsInviteModalOpen(true); };
  
  const handleInviteSubmit = async () => {
    if (!inviteFormData.name || !inviteFormData.email || !inviteFormData.password) {
      setSnackbar({ open: true, message: 'Fill all fields.', severity: 'error' }); return;
    }
    setInviteLoading(true);
    try {
      const newUser = await inviteUser(inviteFormData);
      setUsers(currentUsers => [...currentUsers, newUser]);
      setIsInviteModalOpen(false);
      setSnackbar({ open: true, message: `Invited ${newUser.name}!`, severity: 'success' });
      sessionStorage.removeItem('alfresko_settings_data');
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Invitation failed.', severity: 'error' });
    } finally { setInviteLoading(false); }
  };
  
  const renderUserManagement = () => (
    <Box sx={{ position: 'relative', minHeight: 500 }}>
    {usersLoading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <CircularProgress />
      </Box>
    ) : (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">{t('settings.users.title')}</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenInviteModal} sx={{ bgcolor: '#2563eb' }}>{t('settings.users.inviteUser')}</Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}><TableRow><TableCell>{t('settings.users.tableUser')}</TableCell><TableCell>{t('settings.users.tableEmail')}</TableCell><TableCell>{t('settings.users.tableRole')}</TableCell><TableCell>{t('settings.users.tableActions')}</TableCell></TableRow></TableHead>
            <TableBody>{users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell><Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}><Avatar src={user.avatar}>{(user.name || '?')[0]}</Avatar><Typography>{user.name}</Typography></Box></TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell><Chip label={user.role} color={user.role === 'ADMIN' ? 'primary' : 'default'} size="small" /></TableCell>
                  <TableCell><IconButton onClick={(e) => handleMenuClick(e, user)}><MoreVert /></IconButton></TableCell>
                </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleRoleMenuOpen}>{t('settings.users.changeRole')}</MenuItem>
          <MenuItem onClick={handleRemoveUser} sx={{ color: 'error.main' }}>{t('settings.users.removeUser')}</MenuItem>
        </Menu>
        <Menu anchorEl={roleMenuAnchorEl} open={Boolean(roleMenuAnchorEl)} onClose={handleRoleMenuClose}>
          <MenuItem onClick={() => handleChangeRole('ADMIN')}>{t('settings.users.roleAdmin')}</MenuItem> 
          <MenuItem onClick={() => handleChangeRole('USER')}>{t('settings.users.roleMember')}</MenuItem>
        </Menu>
      </>
    )}
  </Box>
  );

  const renderProductTable = (products, productType, displayName) => {
  // Subtle icons for a premium feel
  const getIcon = () => {
    if (productType === 'base') return <Inventory sx={{ color: '#a8851d', fontSize: '1.2rem', mr: 2 }} />;
    if (productType === 'wall') return <Business sx={{ color: '#a8851d', fontSize: '1.2rem', mr: 2 }} />;
    return <Add sx={{ color: '#a8851d', fontSize: '1.2rem', mr: 2 }} />;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eef2f6', borderRadius: '12px', overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem', py: 2, letterSpacing: '0.5px' }}>
                {t('settings.products.tableName')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem', py: 2, letterSpacing: '0.5px' }}>
                {t('settings.products.tablePrice')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: '0.7rem', py: 2, pr: 3 }}>
                {/* Actions */}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow 
                key={product.id} 
                hover 
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: '0.2s', '&:hover': { bgcolor: '#fdfbf7' } }}
              >
                <TableCell sx={{ py: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getIcon()}
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>
                      {translateName(product.name, lang)}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell align="right">
                  {productType === 'wall' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>FRONT</Typography>
                        <Chip 
                          label={`£${product.frontPrice}`} 
                          size="small" 
                          sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 700, borderRadius: '6px', height: '24px' }} 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>SIDES</Typography>
                        <Chip 
                          label={`£${product.sidesPrice}`} 
                          size="small" 
                          sx={{ bgcolor: '#f0f9ff', color: '#075985', fontWeight: 700, borderRadius: '6px', height: '24px' }} 
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ fontWeight: 800, color: '#059669', fontSize: '1.05rem' }}>
                      £{parseFloat(product.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="right" sx={{ pr: 2 }}>
                  <IconButton 
                    onClick={(e) => handleProductMenuClick(e, product, productType)}
                    sx={{ color: '#94a3b8', '&:hover': { color: '#a8851d', bgcolor: '#fdfaf5' } }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

  const renderProductCatalog = () => (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>{t('settings.products.title')}</Typography>
      <Paper sx={{ p: 2 }}>
        <Tabs value={productTab} onChange={(e, v) => setProductTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('settings.products.tabs.baseConfigs')} />
          <Tab label={t('settings.products.tabs.walls')} />
          <Tab label={t('settings.products.tabs.accessories')} />
        </Tabs>
        {productTab === 0 && renderProductTable(bases, 'base', 'Base Configuration')}
        {productTab === 1 && renderProductTable(walls, 'wall', 'Wall')}
        {productTab === 2 && renderProductTable(accessories, 'accessory', 'Accessory')}
      </Paper>
      <Menu anchorEl={productMenuAnchor} open={Boolean(productMenuAnchor)} onClose={handleProductMenuClose}>
        <MenuItem onClick={handleUpdatePrice}><AttachMoney sx={{ mr: 1 }} />{t('settings.products.updatePrice')}</MenuItem>
      </Menu>
    </Box>
  );

  const renderCompanyProfile = () => (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>{t('settings.profile.title')}</Typography>
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Box mb={3}>
                <Typography fontWeight="bold" color="primary" mb={1}>{t('settings.profile.identity')}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}><TextField fullWidth label={t('settings.profile.legalName')} value={companyProfile.name} disabled /></Grid>
                    <Grid item xs={12}><TextField fullWidth label={t('settings.profile.brandName')} value={companyProfile.brandName} onChange={(e) => handleProfileChange('brandName', e.target.value)} /></Grid>
                </Grid>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box mb={3}>
                <Typography fontWeight="bold" color="primary" mb={1}>{t('settings.profile.contact')}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}><TextField fullWidth label={t('settings.profile.phone')} value={companyProfile.phone} onChange={(e) => handleProfileChange('phone', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Phone fontSize="small"/></InputAdornment> }} /></Grid>
                    <Grid item xs={6}><TextField fullWidth label={t('settings.profile.website')} value={companyProfile.website} onChange={(e) => handleProfileChange('website', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Language fontSize="small"/></InputAdornment> }} /></Grid>
                    <Grid item xs={12}><TextField fullWidth label={t('settings.profile.address')} multiline rows={2} value={companyProfile.address} onChange={(e) => handleProfileChange('address', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn fontSize="small"/></InputAdornment> }} /></Grid>
                </Grid>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
                <Typography fontWeight="bold" color="primary" mb={1}>{t('settings.profile.financial')}</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}><TextField fullWidth label={t('settings.profile.vatId')} value={companyProfile.vatNumber} onChange={(e) => handleProfileChange('vatNumber', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Receipt fontSize="small"/></InputAdornment> }} /></Grid>
                    <Grid item xs={6}><TextField fullWidth label={t('settings.profile.currency')} value={companyProfile.currencySymbol} onChange={(e) => handleProfileChange('currencySymbol', e.target.value)} /></Grid>
                </Grid>
            </Box>
            <Button variant="contained" size="large" onClick={handleSaveProfile} sx={{ mt: 3, bgcolor: '#059669' }}>{t('settings.profile.saveChanges')}</Button>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ textAlign: 'center', p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>{t('settings.profile.logo')}</Typography>
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, border: '1px dashed #ccc' }}>
                {companyProfile.logoUrl ? <img src={companyProfile.logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%' }} /> : <Business sx={{ fontSize: 60, color: '#ccc' }} />}
              </Box>
              <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                {t('settings.profile.uploadLogo')} <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  const renderActiveModule = () => {
    switch (activeModule) {
      case 'users': return isAdmin ? renderUserManagement() : <Typography>Access Denied</Typography>;
      case 'products': return isAdmin ? renderProductCatalog() : <Typography>Access Denied</Typography>;
      case 'profile': return isAdmin ? renderCompanyProfile() : <Typography>Access Denied</Typography>;
      case 'security': return <ChangePasswordForm />; 
      default: return <ChangePasswordForm />;
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedProduct || newPrice === '') return;
    const priceAsNumber = parseFloat(newPrice);
    if (isNaN(priceAsNumber)) { setSnackbar({ open: true, message: 'Invalid price.', severity: 'error' }); return; }
    try {
      if (selectedProductType === 'accessory') {
           await updateAccessoryPrice(selectedProduct.id, priceAsNumber);
           setAccessories(c => c.map(i => i.id === selectedProduct.id ? { ...i, price: priceAsNumber } : i));
      } else if (selectedProductType === 'wall') {
            const pid = selectedPosition === 'front' ? selectedProduct.frontPriceId : selectedProduct.sidesPriceId;
            await updateWallPrice(pid, priceAsNumber);
            setWalls(c => c.map(i => i.id === selectedProduct.id ? (selectedPosition === 'front' ? { ...i, frontPrice: priceAsNumber } : { ...i, sidesPrice: priceAsNumber }) : i));
      } else {
            await updateBasePrice(selectedProduct.id, priceAsNumber);
            setBases(c => c.map(i => i.id === selectedProduct.id ? { ...i, price: priceAsNumber } : i));
      }
      setSnackbar({ open: true, message: `Updated!`, severity: 'success' });
      sessionStorage.removeItem('alfresko_settings_data');
      handleClosePriceEditor();
    } catch (e) { setSnackbar({ open: true, message: 'Failed.', severity: 'error' }); }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Paper sx={{ width: 280, boxShadow: 3, bgcolor: '#ffffff', borderRadius: 0, borderRight: '1px solid #e2e8f0' }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>{t('settings.title')}</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>{t('settings.subtitle')}</Typography>
        </Box>
        <List sx={{ px: 2 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.id} button onClick={() => setActiveModule(item.id)} sx={{ borderRadius: 2, mb: 1, bgcolor: activeModule === item.id ? '#eff6ff' : 'transparent' }}>
              <ListItemIcon sx={{ color: activeModule === item.id ? '#2563eb' : '#64748b' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} sx={{ '& .MuiListItemText-primary': { fontWeight: activeModule === item.id ? 600 : 400, color: activeModule === item.id ? '#2563eb' : '#374151' } }} />
            </ListItem>
          ))}
          <Divider sx={{ my: 2 }} />
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2 }}>
              <ListItemIcon sx={{ color: 'error.main' }}><Logout /></ListItemIcon>
              <ListItemText primary={t('nav.logout')} sx={{ color: 'error.main' }} />
          </ListItemButton>
        </List>
      </Paper>

      <Box sx={{ flex: 1, p: 4 }}>
        {renderActiveModule()}
      </Box>
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          bgcolor: 'rgba(0,0,0,0.7)', 
          zIndex: 9999 
        }}>
          <Box sx={{ textAlign: 'center', bgcolor: 'white', p: 4, borderRadius: 2, boxShadow: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Loading...</Typography>
          </Box>
        </Box>
      )}

      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError('')}
          sx={{ 
            position: 'fixed', 
            top: 80, 
            right: 20, 
            maxWidth: 400,
            zIndex: 9999,
            boxShadow: 3
          }}
        >
          {error}
        </Alert>
      )}

      {/* Existing Snackbar for success messages */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      
      <Dialog open={isPriceEditorOpen} onClose={() => setIsPriceEditorOpen(false)}>
        <DialogTitle>{t('settings.priceEditor.title')}</DialogTitle>
        <DialogContent>
            <DialogContentText mb={2}>{t('settings.priceEditor.body')} <b>{selectedProduct?.name}</b></DialogContentText>
            {selectedProductType === 'wall' && (
                <ToggleButtonGroup value={selectedPosition} exclusive onChange={(e, v) => { if(v) { setSelectedPosition(v); setNewPrice(v==='front' ? selectedProduct?.frontPrice : selectedProduct?.sidesPrice); }}} fullWidth sx={{ mb: 2 }}>
                    <ToggleButton value="front">{t('settings.priceEditor.front')}</ToggleButton><ToggleButton value="sides">{t('settings.priceEditor.sides')}</ToggleButton>
                </ToggleButtonGroup>
            )}
            <TextField autoFocus fullWidth type="number" label="Price" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} />
        </DialogContent>
        <DialogActions><Button onClick={handleClosePriceEditor}>{t('settings.priceEditor.cancel')}</Button><Button variant="contained" onClick={handleSaveChanges}>{t('settings.priceEditor.save')}</Button></DialogActions>
      </Dialog>

      <Dialog open={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)}>
        <DialogTitle>{t('settings.invite.title')}</DialogTitle>
        <DialogContent>
            <TextField autoFocus margin="dense" label={t('settings.invite.name')} fullWidth value={inviteFormData.name} onChange={(e) => setInviteFormData({ ...inviteFormData, name: e.target.value })} />
            <TextField margin="dense" label={t('settings.invite.email')} fullWidth value={inviteFormData.email} onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })} />
            <TextField margin="dense" label={t('settings.invite.password')} type="password" fullWidth value={inviteFormData.password} onChange={(e) => { setInviteFormData({ ...inviteFormData, password: e.target.value }); validatePassword(e.target.value); }} error={inviteFormData.password.length > 0 && !passwordValidation.isValid} helperText={inviteFormData.password.length > 0 && !passwordValidation.isValid ? t('settings.invite.tooWeak') : ""} />
        </DialogContent>
        <DialogActions><Button onClick={() => setIsInviteModalOpen(false)}>{t('settings.invite.cancel')}</Button><Button variant="contained" onClick={handleInviteSubmit} disabled={inviteLoading}>{inviteLoading ? t('settings.invite.inviting') : t('settings.invite.invite')}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;